import { useCallback, useMemo, useState } from 'react';
import { LanguageContext } from './language-context';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, translations } from './translations';

const getInitialLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  const stored = window.localStorage.getItem('lang');
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  const browser = window.navigator.language?.slice(0, 2);
  return SUPPORTED_LANGUAGES.includes(browser) ? browser : DEFAULT_LANGUAGE;
};

const resolveKey = (dict, key) => {
  const parts = key.split('.');
  let value = dict;
  for (const part of parts) {
    if (value == null || typeof value !== 'object') return undefined;
    value = value[part];
  }
  return value;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    setLanguage(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('lang', next);
      document.documentElement.lang = next;
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLang(language === 'es' ? 'en' : 'es');
  }, [language, setLang]);

  const t = useCallback(
    (key) => {
      const value = resolveKey(translations[language], key);
      if (value === undefined) {
        const fallback = resolveKey(translations[DEFAULT_LANGUAGE], key);
        return fallback === undefined ? key : fallback;
      }
      return value;
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, setLanguage: setLang, toggleLanguage, t }),
    [language, setLang, toggleLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export default LanguageProvider;
